import React, { useMemo } from 'react';

import { Space, Table } from 'antd-v5';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { useMBDict } from '@dict/duck/hooks';
import CommentsButton from '@modules/advancement/unitAdvancement/components/CommentsButton/CommentsButton';
import { advancementTypes } from '@shared/constants';
import { getPreferredName } from '@shared/utils/preferredName';

import { filteredToApproveMBsSel } from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import MBImage from './MBImage';
import styles from './MeritBadgesTable.module.scss';

export default function MeritBadgesTable() {
  const { meritBadgesById } = useMBDict();
  const dispatch = useDispatch();

  type RowItem = (typeof meritBadges)[number];
  const meritBadges = useSelector(filteredToApproveMBsSel);

  const rowSelection = useMemo(
    () => ({
      onChange(selectedRowKeys: (string | number)[]) {
        dispatch(
          mbcDashboardActions.setSelectedMeritBadges(
            selectedRowKeys as string[],
          ),
        );
      },
    }),
    [dispatch],
  );

  return (
    <Table
      dataSource={meritBadges}
      rowKey={'rowKey'}
      rowSelection={rowSelection}
      scroll={{ x: true }}
      columns={[
        {
          title: <FormattedMessage id="mbcDashboard.youthTable.column.name" />,
          fixed: 'left',
          sorter: (rowA: RowItem, rowB: RowItem) =>
            rowA.youth.lastName.localeCompare(rowB.youth.lastName),
          render(_value, record) {
            return (
              <Text className={styles.text}>
                {getPreferredName(record.youth)}
              </Text>
            );
          },
        },
        {
          title: <FormattedMessage id="mbcDashboard.youthTable.column.mid" />,
          minWidth: 120,
          sorter: (rowA, rowB) =>
            rowA.youth.memberId[0] - rowB.youth.memberId[0],
          render(_value, record) {
            return (
              <Text className={styles.text}>{record.youth.memberId[0]}</Text>
            );
          },
        },
        {
          title: <FormattedMessage id="mbcDashboard.mbTable.column.mb" />,
          minWidth: 110,
          sorter: (rowA, rowB) => rowA.mb.name.localeCompare(rowB.mb.name),
          render(_value, record) {
            const mbRef = meritBadgesById.get(record.mb.meritBadgeId);
            const mbImage = mbRef?.imageUrl100;
            const mbName = record.mb.name;

            return (
              <Space>
                <MBImage src={mbImage} width={32} height={32} />
                <Text className={styles.text}>{mbName}</Text>
              </Space>
            );
          },
        },
        {
          key: 'commentButton',
          dataIndex: 'commentButton',
          width: '80px',
          onCell: () => ({
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          }),
          render(_value, record) {
            return (
              <CommentsButton
                advancementId={record.mb.meritBadgeId}
                advancementType={advancementTypes.MERIT_BADGES}
                renderPhotos
                userId={record.youth.userId}
                versionId={record.mb.versionId}
              />
            );
          },
        },
        {
          title: (
            <FormattedMessage id="mbcDashboard.mbTable.column.completion" />
          ),
          minWidth: 80,
          sorter: (rowA, rowB) =>
            DateTime.fromISO(rowA.mb.dateCompleted!).valueOf() -
            DateTime.fromISO(rowB.mb.dateCompleted!).valueOf(),
          render(_value, record) {
            const completionDate = record.mb.dateCompleted;

            return <Text className={styles.text}>{completionDate}</Text>;
          },
        },
      ]}
    />
  );
}
